import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/lib/auth';
import { apiFetch } from '@/lib/api/client';
import type { CheckoutResult } from '@/lib/razorpay';
import { supabase } from '@/lib/supabase';

/**
 * Wallet and payments.
 *
 * These go through our API rather than straight to Supabase, unlike the
 * catalogue reads. Money is the one place RLS is not enough: crediting a
 * wallet needs the service role, and a browser must never hold that.
 */

export interface CoinPack {
  code: string;
  price_paise: number;
  coins: number;
  bonus_coins: number;
  tag: string | null;
}

export interface PaymentOrder {
  id: string;
  purpose: 'coin_purchase' | 'user_platform_fee' | 'companion_platform_fee';
  amount_paise: number;
  coins: number | null;
  bonus_coins: number;
  status: string;
  gateway: string;
  created_at: string;
}

export interface FeeStatus {
  userFeePaid: boolean;
  userFeePaise: number;
  companionFeePaid: boolean;
  companionFeePaise: number;
}

export interface WalletState {
  balanceCoins: number;
  purchasedCoins: number;
  bonusCoins: number;
  orders: PaymentOrder[];
  fees: FeeStatus;
  /** True while the dev stub is settling payments instead of a real gateway. */
  mockPayments: boolean;
}

export interface CreatedOrder {
  orderId: string;
  gatewayOrderId: string;
  /** Cashfree only — its SDK takes this instead of the order id. */
  paymentSessionId?: string;
  provider: 'mock' | 'razorpay' | 'cashfree';
  amountPaise: number;
  coins: number | null;
  bonusCoins: number;
  purpose: PaymentOrder['purpose'];
  mock: boolean;
}

export interface SettleResult {
  purpose: PaymentOrder['purpose'];
  coinsCredited: number;
  balanceCoins: number | null;
}

/* ── One wallet, shared ─────────────────────────────────────────────────────
 *
 * useWallet used to be a plain useState per component, and five components
 * call it: the header coin count, the wallet page, the ₹199 paywall, the
 * booking affordability check on a companion profile, and the companion
 * dashboard. Each held its own copy, so paying for something refreshed
 * exactly one of them.
 *
 * The visible symptom was stale coins in the header. The worse one was the
 * paywall: pay the ₹199 and RequireBrowseFee could keep blocking the
 * directory until a manual refresh, because nothing told it anything had
 * changed.
 *
 * A module-level store rather than a context provider, for two reasons: every
 * caller keeps the same import, and a hook that works outside any provider
 * cannot be broken by someone mounting a component in the wrong place.
 */

interface WalletStore {
  wallet: WalletState | null;
  loading: boolean;
  error: string | null;
  /** Whose wallet this is, so another account's balance is never shown. */
  userId: string | null;
}

let store: WalletStore = { wallet: null, loading: true, error: null, userId: null };
const listeners = new Set<() => void>();

// Replaced, never mutated: useSyncExternalStore compares by reference and a
// mutated object would look unchanged.
const setStore = (next: Partial<WalletStore>) => {
  store = { ...store, ...next };
  for (const l of listeners) l();
};

/**
 * In-flight request, shared.
 *
 * Five components mounting together would otherwise fire five identical
 * requests, and the last to land wins — which is also the one most likely to
 * be stale.
 */
let inflight: Promise<void> | null = null;

export function refreshWallet(userId: string | null): Promise<void> {
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const wallet = await apiFetch<WalletState>('/api/wallet');
      setStore({ wallet, error: null, loading: false, userId });
    } catch (err) {
      setStore({
        error: err instanceof Error ? err.message : 'Could not load your wallet.',
        loading: false,
      });
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

/*
 * Sign-out needs no explicit clear. The store records WHOSE wallet it holds,
 * and useWallet hands back null whenever that does not match the current
 * session — so a signed-out user sees nothing and a different account
 * triggers a refetch rather than briefly showing the previous one's coins.
 *
 * Doing it the other way, with auth.tsx calling a clearWallet() here, would
 * make the two modules import each other.
 */

export function useWallet() {
  const { session } = useAuth();
  const userId = session?.user.id ?? null;
  const snapshot = useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => store,
    () => store,
  );

  useEffect(() => {
    if (!userId) return;
    // Fetch when there is nothing yet, or when the account has changed.
    if (snapshot.wallet === null || snapshot.userId !== userId) void refreshWallet(userId);
  }, [userId, snapshot.wallet, snapshot.userId]);

  const refresh = useCallback(() => refreshWallet(userId), [userId]);

  return {
    wallet: snapshot.userId === userId ? snapshot.wallet : null,
    loading: snapshot.loading,
    error: snapshot.error,
    refresh,
  };
}

export function useCoinPacks() {
  const [packs, setPacks] = useState<CoinPack[]>([]);

  useEffect(() => {
    let active = true;
    void apiFetch<{ packs: CoinPack[] }>('/api/wallet/packs')
      .then((r) => {
        if (active) setPacks(r.packs);
      })
      .catch(() => {
        /* the page shows its own error from useWallet */
      });
    return () => {
      active = false;
    };
  }, []);

  return packs;
}

/**
 * Coin packs for a page that has no session — the public pricing section.
 *
 * Goes straight to Supabase rather than through our API, because `coin_packs`
 * is granted to `anon` on purpose: a visitor has to be able to see what things
 * cost before deciding to sign up. It is a price list, and it is the same rows
 * the wallet charges against, so marketing copy cannot drift from reality.
 */
export function usePublicCoinPacks() {
  const [packs, setPacks] = useState<CoinPack[]>([]);

  useEffect(() => {
    let active = true;
    void supabase
      .from('coin_packs')
      .select('code, price_paise, coins, bonus_coins, tag')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data }) => {
        if (active && data) setPacks(data as unknown as CoinPack[]);
      });
    return () => {
      active = false;
    };
  }, []);

  return packs;
}

export const createOrder = (body: { purpose: 'coin_purchase'; packCode: string } | { purpose: 'user_platform_fee' | 'companion_platform_fee' }) =>
  apiFetch<CreatedOrder>('/api/wallet/orders', { method: 'POST', body });

/**
 * Settle a mock order.
 *
 * Only reachable while the API runs with PAYMENT_PROVIDER=mock; in production
 * the API refuses to boot in that configuration, so this call 404s. When
 * Razorpay lands, this function is replaced by the checkout handoff and
 * nothing else in the flow changes.
 */
export const confirmMockOrder = (orderId: string) =>
  apiFetch<SettleResult>(`/api/wallet/orders/${orderId}/confirm`, { method: 'POST' });

/**
 * Hand a completed Razorpay checkout back to the server to be verified.
 *
 * The three fields come from the browser and none of them are trusted: the
 * server re-derives the signature from its own key secret, and checks the
 * order id is the one it opened, before crediting anything.
 *
 * This is the FAST path, not the authoritative one. Razorpay's webhook settles
 * the same order server-to-server and arrives whether or not this call is ever
 * made — which is what covers the user who closes the tab on the success
 * screen. Both converge on the same idempotent settle.
 */
export const verifyOrder = (orderId: string, result: CheckoutResult) =>
  apiFetch<SettleResult>(`/api/wallet/orders/${orderId}/verify`, {
    method: 'POST',
    body: result,
  });

/**
 * Wait for an order to actually be settled, then hand back the fresh wallet.
 *
 * Only Cashfree needs this. Its checkout brings back no signed payload, so
 * nothing in the browser can prove a payment happened — the webhook is the
 * only authority, and it lands a moment after the sheet closes.
 *
 * POLLS THE ORDER, NOT THE BALANCE. The first version watched `balanceCoins`
 * and worked fine for coin purchases and not at all for platform fees, which
 * settle without crediting a single coin: the balance never moved, the poll
 * ran its full timeout, and the user was told their payment might not have
 * arrived. `status === 'paid'` is true for both.
 *
 * Gives up rather than spinning forever — the money may still arrive, and the
 * wording at the call site says so.
 */
export async function waitForOrderPaid(
  orderId: string,
  attempts = 10,
  delayMs = 1000,
): Promise<WalletState | null> {
  for (let i = 0; i < attempts; i += 1) {
    await new Promise((r) => setTimeout(r, delayMs));
    try {
      const w = await apiFetch<WalletState>('/api/wallet');
      if (w.orders.some((o) => o.id === orderId && o.status === 'paid')) return w;
    } catch {
      // A blip mid-poll is not a failed payment. Keep trying.
    }
  }
  return null;
}

/** What an order credited, once it is paid. Zero for a platform fee. */
export const coinsFromOrder = (wallet: WalletState, orderId: string): number => {
  const o = wallet.orders.find((x) => x.id === orderId);
  return o ? (o.coins ?? 0) + o.bonus_coins : 0;
};

export const rupees = (paise: number) => `₹${(paise / 100).toLocaleString('en-IN')}`;
